import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { UserNotificationDTO } from "@/src/types/types";

import {
  NotificationQueryKeys,
  deleteNotification,
  getNotifications,
  updateNotification
} from "./notificationFunctions";

export const useGetNotifications = (isAuthenticated: boolean) => {
  return useQuery({
    queryKey: [NotificationQueryKeys.NOTIFICATIONS],
    queryFn: () => getNotifications(),
    select: (notifications) =>
      notifications.sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      ),
    enabled: isAuthenticated
  });
};

export const useUpdateNotification = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateNotification,
    // update change to cache immediately, invalidate query after mutation has settled
    // https://tanstack.com/query/v4/docs/react/guides/optimistic-updates
    onMutate: async ({ notificationId, isRead }) => {
      await queryClient.cancelQueries({
        queryKey: [NotificationQueryKeys.NOTIFICATIONS]
      });
      const previousNotifications = queryClient.getQueryData([
        NotificationQueryKeys.NOTIFICATIONS
      ]) as UserNotificationDTO[] | undefined;

      // If no previous notifications data, skip optimistic update
      if (!previousNotifications) {
        return {
          previousNotifications: undefined,
          newNotifications: undefined
        };
      }

      const newNotifications = [...previousNotifications];
      const oldNotificationIndex = previousNotifications.findIndex(
        ({ id }) => notificationId === id
      );
      if (oldNotificationIndex !== -1) {
        newNotifications[oldNotificationIndex] = {
          ...previousNotifications[oldNotificationIndex],
          isRead
        };
        queryClient.setQueryData(
          [NotificationQueryKeys.NOTIFICATIONS],
          newNotifications
        );
      }

      return {
        previousNotifications,
        newNotifications
      };
    },
    onError: (error, variables, context) => {
      // Rollback optimistic updates on error
      if (context?.previousNotifications) {
        queryClient.setQueryData(
          [NotificationQueryKeys.NOTIFICATIONS],
          context.previousNotifications
        );
      }
    },
    // Triggers on both onError & onSuccess
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: [NotificationQueryKeys.NOTIFICATIONS]
      });
    }
  });
};

export const useDeleteNotification = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteNotification,
    onError: () => {
      queryClient.invalidateQueries({
        queryKey: [NotificationQueryKeys.NOTIFICATIONS]
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [NotificationQueryKeys.NOTIFICATIONS]
      });
    }
  });
};
